import type { FederalState } from '../../domain/holidays';
import type { CategoryId } from '../../domain/types';
import type { Appearance } from '../state/app-data';
import { de } from './de';
import { uk } from './uk';

export type Language = 'uk' | 'de';

/**
 * DE: Alle Oberflächentexte einer Sprache; beide Sprachen müssen jeden Schlüssel füllen.
 * EN: All UI texts of one language; both languages must fill every key.
 */
export interface Messages {
  tabToday: string;
  tabPlan: string;
  tabShopping: string;
  settings: string;
  /**
   * DE: Namen der beiden voreingestellten Personen.
   * EN: Names of the two preset household members.
   */
  defaultMemberNames: readonly string[];
  shoppingTitle: string;
  shoppingToday: string;
  shoppingExtra: string;
  nothingToBuy: string;
  shopsClosedToday: string;
  nextTrip: (date: string, stores: string, total: string) => string;
  itemCount: (count: number) => string;
  total: (total: string) => string;
  totalWithWine: (total: string, wine: string) => string;
  openShoppingList: string;
  amongShopping: string;
  dinnerTitle: string;
  minutes: (count: number) => string;
  activeMinutes: (count: number) => string;
  portions: (count: number) => string;
  fish: string;
  addOn: (member: string, product: string, amount: string) => string;
  allAtHome: string;
  recipe: string;
  noEnergy: string;
  tomorrowTitle: string;
  holidayTomorrow: (holiday: string) => string;
  noPlanTitle: string;
  planMonth: string;
  planWeek: string;
  planEnds: (date: string) => string;
  nextMonth: string;
  nextWeek: string;
  planning: string;
  chooseFederalState: string;
  close: string;
  ingredients: string;
  steps: string;
  noEnergyTitle: string;
  noEnergyHint: string;
  noAlternatives: string;
  movedTo: (date: string) => string;
  useSoon: (products: string) => string;
  planAgain: string;
  planAgainQuestion: string;
  yes: string;
  cancel: string;
  budgetPlanned: (planned: string, limit: string) => string;
  wholePlan: string;
  thisWeek: string;
  weekOf: (date: string, cost: string) => string;
  nextPlanTitle: string;
  quick: string;
  lunchbox: string;
  locked: string;
  breakfast: string;
  lunch: string;
  replace: string;
  replaceTitle: string;
  noReplacement: string;
  perPortion: (price: string) => string;
  lock: string;
  unlock: string;
  lowEnergyDay: string;
  lowEnergyMarked: string;
  /**
   * DE: Sätze zu gelockerten Planungsregeln; das Datum steht davor.
   * EN: Sentences about relaxed planning rules; the date goes in front.
   */
  relaxedWarnings: Readonly<
    Record<'relaxed-quick' | 'relaxed-lunchbox' | 'relaxed-variety', string>
  >;
  overBudget: (amount: string) => string;
  checkAtHome: (products: string) => string;
  markBought: string;
  doneTrips: string;
  allBought: string;
  onOffer: string;
  savings: (amount: string) => string;
  categories: Readonly<Record<CategoryId, string>>;
  languageTitle: string;
  appearanceTitle: string;
  appearanceChoices: Readonly<Record<Appearance, string>>;
  installHint: string;
  installHintDismiss: string;
  federalStateTitle: string;
  federalStateHint: string;
  notChosen: string;
  closedDaysTitle: string;
  closedDaysHint: string;
  addClosedDay: string;
  remove: string;
  noClosedDays: string;
  backupTitle: string;
  backupHint: string;
  downloadBackup: string;
  restoreBackup: string;
  restoreQuestion: string;
  restored: string;
  backupErrors: Readonly<Record<'invalid' | 'newer-version', string>>;
  errorTitle: string;
  errorText: string;
  errorSave: string;
  errorRestart: string;
  /**
   * DE: Feiertagsnamen nach Regel-Kennung (siehe `src/data/holidays-de.ts`).
   * EN: Holiday names by rule id (see `src/data/holidays-de.ts`).
   */
  holidays: Readonly<Record<string, string>>;
  states: Readonly<Record<FederalState, string>>;
}

export const MESSAGES: Readonly<Record<Language, Messages>> = { uk, de };
